import React, { ReactElement, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import casePdfGeneratorStyle from "./casePdfGeneratorStyle";
import { Checkbox, FormControl, FormLabel, ListItemText, Paper } from "@mui/material";
import _ from "lodash";
import {
  getDocumentName,
  getExternalReference,
  getExternalReferenceSelectValue,
  getReferenceOptions,
} from "./casePdfGeneratorService";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import { RadioButtonGroup, useFormContext } from "react-hook-form-mui";
import { MediaObject } from "../../../types/MediaObject";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import { saveSessionStorageState } from "../../../services/browserStorageService";
import { UseForm } from "../../../types/UseForm";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { ProductUnion } from "../../../types/ProductUnion";
import { getPdfGeneratorFormElementConfig } from "../../../services/pdfGeneratorFormService";
import dayjs from "dayjs";
import FormElementReactHookForm from "../CaseForm/FormElementReactHookForm";

type PdfGeneratorFormElementsProps = {
  product: ProductUnion;
  mediaObjects: MediaObject[];
  minimalView?: boolean;
  additionalEditorToolbarActions?: ReactElement[];
};

const documentTemplates = {
  customPdf: "Individueller Schriftsatz",
  extrajudicialLetter: "Außergerichtliches Schreiben (inkl. Vollmacht)",
};

const PdfGeneratorFormElements = ({
  product,
  mediaObjects,
  minimalView = false,
  additionalEditorToolbarActions,
}: PdfGeneratorFormElementsProps) => {
  const currentUser = useCurrentUser();

  const isDocumentTemplateEnabled =
    ([
      "generalCivilLaw",
      "employmentContract",
      "civilServiceLaw",
      "rentalContract",
      "divorce",
      "trafficAccident",
    ].includes(product.productClassName) ||
      product.productCategory === "contractLaw") &&
    !currentUser.isExternal;
  const formContext = useFormContext();

  useEffect(() => {
    saveFormValuesBackup(formContext.getValues(), product);
  }, [formContext.watch()]);

  useEffect(() => {
    formContext.setValue(
      "externalReference",
      getExternalReference(product, formContext.getValues("externalReferenceSelect"))
    );
  }, [formContext.watch("externalReferenceSelect")]);

  useEffect(() => {
    if (formContext.getValues("sendEmailToCustomer") === "true") {
      formContext.setValue("showInGallery", "true");
    }
  }, [formContext.watch("sendEmailToCustomer")]);

  useEffect(() => {
    formContext.setValue("filename", getDocumentName(formContext.getValues("recipientType")));
    formContext.setValue(
      "externalReferenceSelect",
      getExternalReferenceSelectValue(product, formContext.watch("recipientType") || "")
    );
  }, [formContext.watch("recipientType")]);

  const renderSelectedMediaObject = (selected: number[]) => {
    //map over the selected array and find the related mediaobject to get its description
    if (!mediaObjects.length) {
      return "";
    }
    return _.map(
      selected,
      (selectedMediaObject) =>
        _.find(mediaObjects, (mediaObject) => mediaObject.id === selectedMediaObject)?.description
    ).join(", ");
  };

  const referenceOptions = useMemo(
    () => _.filter(getReferenceOptions(product), (option) => !option.isHidden),
    [product]
  );

  const handleRecipientTypeChange = (value: string) => {
    formContext.setValue("recipientType", value);

    switch (value) {
      case "opposingCarLiabilityInsurance":
        formContext.setValue("insurancePolicyNumber", product.opposingInsurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.opposingCaseReferenceNumber || "");
        break;
      case "opposingLiabilityInsurance":
        formContext.setValue("insurancePolicyNumber", product.opposingLiabilityInsurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.opposingLiabilityInsuranceCaseReferenceNumber || "");
        break;
      default:
        formContext.setValue("insurancePolicyNumber", product.caseInsurance.insurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.caseInsurance.caseReferenceNumber || "");
        break;
    }
  };

  const handleDocumentTemplateChange = (value: string) => {
    formContext.setValue("documentTemplate", value);

    if (value === "extrajudicialLetter" && referenceOptions.find((option) => option.type === "opponentLawyer")) {
      formContext.setValue("externalReferenceSelect", "opponentLawyer");
    } else if (
      value === "extrajudicialLetter" &&
      referenceOptions.find((option) => option.type === "opposingPartyReference")
    ) {
      formContext.setValue("externalReferenceSelect", "opposingPartyReference");
    }

    if (value === "extrajudicialLetter") {
      formContext.setValue("electronicFileFolderPath", "Außergerichtliches Verfahren");
    } else {
      formContext.setValue("electronicFileFolderPath", null);
    }
  };

  useEffect(() => {
    handleDocumentTemplateChange(formContext.getValues("documentTemplate"));
  }, []);

  const formElementConfig = getPdfGeneratorFormElementConfig({
    isMinimalView: minimalView,
    formContext,
    product,
    referenceOptions,
    handleRecipientTypeChange,
  });

  return (
    <Grid container spacing={3} sx={{
      alignItems: "flex-start"
    }}>
      {isDocumentTemplateEnabled && (
        <>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <SelectElement
              label={"Typ des Schreibens"}
              name={"documentTemplate"}
              onChange={handleDocumentTemplateChange}
            >
              {Object.entries(documentTemplates).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </SelectElement>
          </Grid>
          <Grid
            sx={{ display: { xs: "none", md: "block" } }}
            size={{
              md: 6
            }}></Grid>
        </>
      )}
      {formElementConfig.map(({ cols, ...config}, idx) => {
        return (
          <Grid
            key={idx + formContext.watch("documentTemplate", "") + formContext.watch("recipientType", "")}
            size={{
              xs: cols?.xs,
              md: cols?.md
            }}>
            <FormElementReactHookForm {...config} product={product} />
          </Grid>
        );
      })}
      <Grid size={12}>
        <Grid container spacing={3} sx={{
          alignItems: "flex-start"
        }}>
          {!minimalView && (
            <>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Für Mandantschaft sichtbar</FormLabel>
                  <RadioButtonGroup
                    row
                    options={[
                      { id: "true", label: "Ja" },
                      { id: "false", label: "Nein", disabled: formContext.watch("sendEmailToCustomer") === "true" },
                    ]}
                    name={"showInGallery"}
                  />
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Mandantschaft benachrichtigen</FormLabel>
                  <RadioButtonGroup
                    row
                    options={[
                      { id: "true", label: "Ja" },
                      { id: "false", label: "Nein" },
                    ]}
                    name={"sendEmailToCustomer"}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <SelectElement
              label={"Dokument anhängen"}
              name={"selectedMediaObjects"}
              multiple
              renderValue={renderSelectedMediaObject}
            >
              {_.map(mediaObjects, (mediaObject) => (
                <MenuItem
                  key={mediaObject.id}
                  value={mediaObject.id}
                  disabled={mediaObject.mimeType !== "application/pdf" || mediaObject.size > 2000000}
                >
                  <Checkbox
                    checked={
                      !!_.find(
                        formContext.watch("selectedMediaObjects"),
                        (selectedMediaObject) => selectedMediaObject === mediaObject.id
                      )
                    }
                  />
                  <ListItemText
                    primary={mediaObject.description + " | " + dayjs(mediaObject.created).format("DD.MM.YYYY")}
                  />
                </MenuItem>
              ))}
            </SelectElement>
          </Grid>
        </Grid>
      </Grid>
      <Grid style={{ width: "100%", maxWidth: 850, margin: "0 auto" }}>
        <Paper sx={casePdfGeneratorStyle.paper} id={"lbEditorAnchor"}>
          <LegalbirdTipTapEditor
            additionalToolbarActions={additionalEditorToolbarActions}
            editorSx={{ border: "none", padding: 0 }}
            sx={{ position: "relative" }}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

const saveFormValuesBackup = _.debounce((values: UseForm["values"], product: ProductUnion) => {
  saveSessionStorageState("customPdfValues" + product.id, values);
}, 3000);

export default PdfGeneratorFormElements;
